import { Circle, LoaderCircle } from 'lucide-react';
import { useEffect } from 'react';
import { useAuth } from '../../contexts/workos-auth-context';

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
  loadingComponent?: React.ReactNode;
}

/**
 * Protected Route Component
 *
 * Wraps routes that require authentication.
 * Redirects unauthenticated users to the specified redirect path (default: /login).
 * Shows a loading component while checking authentication status.
 *
 * Usage:
 * ```tsx
 * import { ProtectedRoute } from '@ophelos/ui/components/auth/protected-route';
 *
 * <Routes>
 *   <Route path="/login" element={<LoginPage />} />
 *   <Route
 *     path="/dashboard"
 *     element={
 *       <ProtectedRoute>
 *         <DashboardPage />
 *       </ProtectedRoute>
 *     }
 *   />
 * </Routes>
 * ```
 */
export function ProtectedRoute({ children, redirectTo = '/login', loadingComponent }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();

  // Use window.location instead of Router hooks to avoid Router context issues with lazy-loaded components
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      const currentPath = window.location.pathname;
      if (currentPath !== redirectTo) {
        window.location.href = redirectTo;
      }
    }
  }, [isLoading, isAuthenticated, redirectTo]);

  if (isLoading) {
    return (
      loadingComponent || (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="relative">
              <Circle className="size-12 text-muted-foreground/25" />
              <LoaderCircle className="absolute -top-0.5 -left-0.5 size-13 animate-spin text-primary" />
            </div>
            <p className="mt-4 text-base text-muted-foreground">Loading...</p>
          </div>
        </div>
      )
    );
  }

  if (!isAuthenticated) {
    // Return null while navigation happens via useEffect
    return null;
  }

  return <>{children}</>;
}
